import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'ramda'
import { DateUtils } from '@pbt/pbt-ui-components'

import DialogNames from '~/constants/DialogNames'
import { useNormalizedProblemsSubscription } from '~/hooks/useNormalizedProblemsSubscription'
import i18n from '~/locales/i18n'
import {
  DiagnosisSnapshotItemType,
  type MasterProblemsSnapshotItem,
} from '~/types'
import useDialog from '~/utils/useDialog'

import { RIGHT_RAIL_DATE_COL_PX } from '../constants'
import PatientHistoryEmptyState from '../PatientHistoryEmptyState'
import PatientHistoryWidget, { type TabData } from '../PatientHistoryWidget'
import ProblemsHistoryCell from './ProblemsHistoryCell'
import ProblemsNameCell from './ProblemsNameCell'
import ProblemsStatusCell from './ProblemsStatusCell'

const problemsColumns = (clientId: string, patientId: string) => [
  {
    id: 'date',
    fixedWidth: RIGHT_RAIL_DATE_COL_PX,
    prop: (item: MasterProblemsSnapshotItem) => DateUtils.formatDate(item.date),
    label: i18n.t('Common:DATE_TIME'),
  },
  {
    id: 'title',
    component: (item: MasterProblemsSnapshotItem) => (
      <ProblemsNameCell item={item} />
    ),
    width: 5,
    label: i18n.t('Common:PROBLEM'),
  },
  {
    id: 'diagnosisHistory',
    component: (item: MasterProblemsSnapshotItem) => (
      <ProblemsHistoryCell
        clientId={clientId}
        item={item}
        patientId={patientId}
      />
    ),
    fixedWidth: 65,
    disableEllipsis: true,
    isAction: true,
  },
  {
    id: 'diagnosisStatus',
    component: (item: MasterProblemsSnapshotItem) => (
      <ProblemsStatusCell
        clientId={clientId}
        item={item}
        patientId={patientId}
      />
    ),
    fixedWidth: 245,
    label: i18n.t('Common:STATUS'),
    disableEllipsis: true,
  },
]

interface ProblemsSummaryWidgetProps {
  clientId: string
  patientId: string
  title?: string
}

const ACTIVE_TAB_ID = 0
const HISTORIC_TAB_ID = 1
const PAGE_SIZE = 5

const ProblemsSummaryWidget = ({
  clientId,
  patientId,
  title = i18n.t('Clients:PROBLEMS_WIDGET.TITLE'),
}: ProblemsSummaryWidgetProps) => {
  const { t } = useTranslation(['Common', 'Clients'])

  const {
    activeProblems,
    historicProblems,
    loading: isLoading,
    AddedByAiDiagnosisStatusId,
  } = useNormalizedProblemsSubscription(patientId)

  const byLatestDate = R.descend((diagnosis: MasterProblemsSnapshotItem) =>
    new Date(diagnosis.date).getTime(),
  )

  const [openDiagnosisHistoryDialog] = useDialog(DialogNames.DIAGNOSIS_HISTORY)
  const [selectedTab, setSelectedTab] = useState(0)
  const [pageByTab, setPageByTab] = useState<Record<number, number>>({
    [ACTIVE_TAB_ID]: 1,
    [HISTORIC_TAB_ID]: 1,
  })

  const handleAddDiagnose = () => {
    openDiagnosisHistoryDialog({ clientId, patientId })
  }

  const noRecords = (active: boolean) => (
    <PatientHistoryEmptyState
      message={
        active
          ? t('Clients:SNAPSHOTS_AND_RECORDS.NO_ACTIVE_DIAGNOSES')
          : t('Clients:SNAPSHOTS_AND_RECORDS.NO_HISTORIC_DIAGNOSES')
      }
    />
  )

  const columns = useMemo(
    () => problemsColumns(clientId, patientId),
    [clientId, patientId],
  )

  // Ensure stable sorting without mutating subscription arrays
  const sortedActive = useMemo(
    () => [...activeProblems].sort(byLatestDate),
    [activeProblems],
  )
  const sortedHistoric = useMemo(
    () => [...historicProblems].sort(byLatestDate),
    [historicProblems],
  )

  const activeTotal = sortedActive.length
  const historicTotal = sortedHistoric.length

  const activeStart = (pageByTab[ACTIVE_TAB_ID] - 1) * PAGE_SIZE
  const historicStart = (pageByTab[HISTORIC_TAB_ID] - 1) * PAGE_SIZE

  const paginatedActive = useMemo(
    () => sortedActive.slice(activeStart, activeStart + PAGE_SIZE),
    [sortedActive, activeStart],
  )
  const paginatedHistoric = useMemo(
    () => sortedHistoric.slice(historicStart, historicStart + PAGE_SIZE),
    [sortedHistoric, historicStart],
  )

  // Clamp pages when list sizes change
  useEffect(() => {
    const totalActivePages = Math.max(1, Math.ceil(activeTotal / PAGE_SIZE))
    const totalHistoricPages = Math.max(1, Math.ceil(historicTotal / PAGE_SIZE))
    const nextActivePage = Math.min(pageByTab[ACTIVE_TAB_ID], totalActivePages)
    const nextHistoricPage = Math.min(
      pageByTab[HISTORIC_TAB_ID],
      totalHistoricPages,
    )

    if (
      nextActivePage !== pageByTab[ACTIVE_TAB_ID] ||
      nextHistoricPage !== pageByTab[HISTORIC_TAB_ID]
    ) {
      setPageByTab((prev) => ({
        ...prev,
        [ACTIVE_TAB_ID]: nextActivePage,
        [HISTORIC_TAB_ID]: nextHistoricPage,
      }))
    }
  }, [activeTotal, historicTotal, pageByTab])

  // Reset pages when patient changes
  useEffect(() => {
    setPageByTab({ [ACTIVE_TAB_ID]: 1, [HISTORIC_TAB_ID]: 1 })
  }, [patientId])

  const tabs: TabData<MasterProblemsSnapshotItem>[] = [
    {
      id: ACTIVE_TAB_ID,
      label: t('Clients:PROBLEMS_WIDGET.ACTIVE_CHRONIC'),
      labelCount: isLoading
        ? undefined
        : activeProblems.filter(
            (problem) =>
              problem.statusId !== AddedByAiDiagnosisStatusId ||
              problem.type !== DiagnosisSnapshotItemType.DIAGNOSIS_HISTORY,
          ).length,
      data: paginatedActive,
      columns,
      noRecords: noRecords(true),
      totalCount: activeTotal,
    },
    {
      id: HISTORIC_TAB_ID,
      label: t('Clients:PROBLEMS_WIDGET.HISTORIC'),
      labelCount: isLoading
        ? undefined
        : historicProblems.filter(
            (problem) =>
              problem.statusId !== AddedByAiDiagnosisStatusId ||
              problem.type !== DiagnosisSnapshotItemType.DIAGNOSIS_HISTORY,
          ).length,
      data: paginatedHistoric,
      columns,
      noRecords: noRecords(false),
      totalCount: historicTotal,
    },
  ]

  return (
    <PatientHistoryWidget
      currentPage={pageByTab[selectedTab]}
      headerAction={{
        text: t('Common:ADD_DIAGNOSIS_HISTORY'),
        onClick: handleAddDiagnose,
      }}
      isLoading={isLoading}
      layoutOptions={{
        flushRows: true,
      }}
      maxRows={PAGE_SIZE}
      selectedTab={selectedTab}
      tabs={tabs}
      title={title}
      onPageChange={(page) =>
        setPageByTab((prev) => ({ ...prev, [selectedTab]: page }))
      }
      onSelectedTabChange={setSelectedTab}
    />
  )
}

export default ProblemsSummaryWidget
